import supabase from "./supabase";
import { PREDEFINED_SCALES } from "../components/pages/teacher/GradeScalesManager";

export interface CreateGradeScaleParams {
  name: string;
  values: any;
}

export async function createGrade(
  data: CreateGradeScaleParams
): Promise<{ error: string | null; grade_scale?: any }> {
  try {
    const { data: grade_scale, error } = await supabase.functions.invoke(
      "createGrade",
      {
        body: data,
      }
    );

    if (error) {
      console.error("Error creating grade scale:", error);
      return { error: error.message };
    }

    // If the function returns a JSON string, parse it
    let parsed = grade_scale;
    if (typeof grade_scale === "string") {
      try {
        parsed = JSON.parse(grade_scale);
      } catch {}
    }
    // The backend returns { grade_scale: ... }
    if (parsed && parsed.grade_scale) {
      return { error: null, grade_scale: parsed.grade_scale };
    }
    return { error: null, grade_scale: parsed };
  } catch (err: any) {
    console.error("Error creating grade scale:", err);
    return { error: err.message };
  }
}

export async function getStudentGradeType({
  assignment,
  userId,
}: {
  assignment: any;
  userId: string;
}): Promise<string> {
  let gradeType = "-";
  let studentClass = null;

  if (assignment) {
    if (
      assignment.assign_to === "class" &&
      Array.isArray(assignment.assigned_class) &&
      assignment.assigned_class.length > 0
    ) {
      const classIds = assignment.assigned_class.map((cls) => cls.id);
      const { data: classStudent, error: classStudentError } = await supabase
        .from("class_students")
        .select("class_id")
        .eq("user_id", userId)
        .in("class_id", classIds)
        .maybeSingle();
      if (!classStudentError && classStudent && classStudent.class_id) {
        studentClass = assignment.assigned_class.find(
          (cls) => cls.id === classStudent.class_id
        );
        if (studentClass && studentClass.grade_type) {
          gradeType = studentClass.grade_type;
        }
      }
    } else if (assignment.assign_to === "individuals") {
      const { data: classStudent, error: classStudentError } = await supabase
        .from("class_students")
        .select("class_id")
        .eq("user_id", userId)
        .maybeSingle();
      if (!classStudentError && classStudent && classStudent.class_id) {
        if (
          assignment.assigned_class &&
          Array.isArray(assignment.assigned_class)
        ) {
          studentClass = assignment.assigned_class.find(
            (cls) => cls.id === classStudent.class_id
          );
        }
        if (!studentClass) {
          const { data: classObj, error: classObjError } = await supabase
            .from("classes")
            .select("id, grade_type")
            .eq("id", classStudent.class_id)
            .maybeSingle();
          if (!classObjError && classObj && classObj.grade_type) {
            gradeType = classObj.grade_type;
          }
        } else if (studentClass.grade_type) {
          gradeType = studentClass.grade_type;
        }
      }
    }
  }
  if (gradeType === "-" && assignment && assignment.grade_type) {
    gradeType = assignment.grade_type;
  }
  return gradeType;
}

export async function updateGrade(
  id: string,
  user_id: string,
  data: Partial<CreateGradeScaleParams>
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase
      .from("grade_scales")
      .update(data)
      .eq("id", id)
      .eq("teacher_id", user_id);

    if (error) {
      console.error("Error updating grade scale:", error);
      return { error: error.message };
    }

    return { error: null };
  } catch (err: any) {
    console.error("Error updating grade scale:", err);
    return { error: err.message };
  }
}

export async function deleteGrade(
  id: string,
  user_id: string
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase
      .from("grade_scales")
      .delete()
      .eq("id", id)
      .eq("teacher_id", user_id);

    if (error) {
      console.error("Error deleting grade scale:", error);
      return { error: error.message };
    }

    return { error: null };
  } catch (err: any) {
    console.error("Error deleting grade scale:", err);
    return { error: err.message };
  }
}

export async function getGrades(
  user_id: string
): Promise<{ grades: any[]; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from("grade_scales")
      .select("*")
      .eq("teacher_id", user_id);
    if (error) {
      console.error("Error fetching grade scales:", error);
      return { grades: [], error: error.message };
    }
    return { grades: data || [], error: null };
  } catch (err: any) {
    console.error("Error fetching grade scales:", err);
    return { grades: [], error: err.message };
  }
}

/**
 * Map a percent score to a grade label using the appropriate grade scale.
 * @param percent - The percent score (0-100)
 * @param gradeType - The name of the grade scale (e.g., "Standard Letter Grade (A-F)")
 * @returns grade label (e.g., "A", "Pass", "3 - Meets Standard", etc.)
 */
export async function mapPercentToGradeLabel(
  percent: number,
  gradeType: string
): Promise<string> {
  let scale = PREDEFINED_SCALES.find((s) => s.name === gradeType);

  if (!scale) {
    // Fetch custom grade scales for this user
    const { data, error } = await supabase
      .from("grade_scales")
      .select("*")
      .eq("name", gradeType)
      .maybeSingle();
    if (!error && data && data.values) {
      scale = data;
    }
  }

  if (!scale || !Array.isArray(scale.values)) {
    return percent.toFixed(1) + "%";
  }
  // Use integer percent for matching (no decimals)
  const intPercent = Math.round(percent);
  const match = scale.values.find(
    (v: any) => intPercent >= v.min && intPercent <= v.max
  );
  if (!match) {
    console.warn(
      "No grade label match for percent",
      intPercent,
      "in scale",
      scale
    );
    return percent.toFixed(1) + "%";
  }
  return match.label;
}

/**
 * Calculates percent correct based on correct answers only.
 * For MC/FB: correct if answer matches.
 * For SR/ST: correct if evaluation[i].passed === true.
 * @param questions - Array of questions
 * @param answers - Object or array of user answers
 * @param evaluations - Object with SR/ST evaluation results (optional)
 * @returns { percent: number, correct: number, total: number }
 */
export function calculatePercentCorrect({
  questions,
  answers,
  evaluations = {},
}: {
  questions: any[];
  answers: Record<string | number, any>;
  evaluations?: Record<string | number, any>;
}): { percent: number; correct: number; total: number } {
  let correct = 0;
  const total = questions.length;
  questions.forEach((q, i) => {
    if (q.type === "MC" && answers[i] !== undefined) {
      if (q.correct && answers[i] === q.correct) correct++;
    } else if (q.type === "FB" && answers[i] !== undefined) {
      // If both correct and answer are arrays (multi-blank)
      if (Array.isArray(q.correct) && Array.isArray(answers[i])) {
        const allCorrect =
          q.correct.length === answers[i].length &&
          q.correct.every(
            (c, idx) =>
              c &&
              answers[i][idx] &&
              answers[i][idx].toString().trim().toLowerCase() ===
                c.toString().trim().toLowerCase()
          );
        if (allCorrect) correct++;
      } else {
        // Single blank
        const corrects = Array.isArray(q.correct) ? q.correct : [q.correct];
        if (
          corrects.some(
            (c) =>
              c &&
              answers[i] &&
              answers[i].toString().trim().toLowerCase() ===
                c.toString().trim().toLowerCase()
          )
        )
          correct++;
      }
    } else if ((q.type === "SR" || q.type === "ST") && evaluations[i]) {
      if (evaluations[i].passed) correct++;
    }
  });
  const percent =
    total > 0 ? parseFloat(((correct / total) * 100).toFixed(2)) : 0;
  return { percent, correct, total };
}
